import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/lib/auth'
import { useI18n } from '@/lib/i18n'
import {
  applyArenaDynamicHead,
  arenaLanguageTag,
  arenaSeoOrigin,
  arenaSeoRobots,
} from '@/lib/arena-dynamic-seo'
import { getStartedGameRoute } from '@/lib/started-game-route'
import { type LanguageCode, withLanguagePath } from '@/lib/url-language-utils'
import {
  formatGameSettingDisplay,
  parseGameSettingToPlayerConfig,
} from '@/lib/play'
import { useTRPC } from '@/lib/trpc'
import {
  useInvalidateSocial,
  useSocialOverview,
  useSocialWorkspace,
} from '@/social/social-provider'
import type { SocialOverview, SocialSearchUser } from '@/social/types'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  ArrowUpRight,
  CalendarDays,
  Check,
  Gamepad2,
  Loader2,
  RefreshCw,
  SearchX,
  Trophy,
  UserPlus,
  UserRound,
  X,
} from 'lucide-react'
import { type ReactNode, useEffect, useLayoutEffect } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { isFixedGameSetting, readSettingMode } from '@repo/sdk'

const PROFILE_LOCALES: Record<string, string> = {
  ar: 'ar-SA',
  en: 'en-US',
  es: 'es-ES',
  fr: 'fr-FR',
  ja: 'ja-JP',
  ko: 'ko-KR',
  tw: 'zh-TW',
  zh: 'zh-CN',
}

function formatStartedAt(value: string | Date, language: string): string {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime()))
    return ''
  return new Intl.DateTimeFormat(PROFILE_LOCALES[language] ?? 'en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date)
}

type RelationshipAction
  = | { action: 'request', targetUserId: string }
    | { action: 'accept' | 'cancel', connectionId: string }
    | { action: 'unblock', targetUserId: string }

export function gameSettingTitle(
  gameSetting: string,
  t: (key: string) => string,
): string {
  if (isFixedGameSetting(gameSetting))
    return t(`gameSettings.${gameSetting}`)
  const settingMode = readSettingMode(gameSetting)
  if (settingMode.terminalSuffix === 'R' || settingMode.markers.containsTrial)
    return t('gameSettings.G10_ULTIMATE_TRIAL')
  if (gameSetting === 'G6_1SR1WT_2WW_2VG')
    return t('gameSettings.G6_1SR1WT_2WW_2VG')
  if (gameSetting === 'G9_1HT1SR1WT_3WW_3VG')
    return t('gameSettings.G9_1HT1SR1WT_3WW_3VG')
  if (settingMode.terminalSuffix === 'W')
    return t('gameSettings.WATCH_MODE')
  if (settingMode.mode === 'comparison')
    return t('gameSettings.MODEL_COMPARISON')
  if (settingMode.terminalSuffix === 'B')
    return t('gameSettings.BYOM')
  return t('gameSettings.USER_CUSTOM_CONFIG')
}

function PublicProfileFriendAction({
  publicUsername,
}: {
  publicUsername: string
}) {
  const { authResolved, user } = useAuth()
  const { t } = useI18n()
  const location = useLocation()
  const trpc = useTRPC()
  const overviewQuery = useSocialOverview(!!user)
  const overview = overviewQuery.data as SocialOverview | undefined
  const viewerUsername = overview?.profile.username
  const isSelf = viewerUsername === publicUsername
  const canSearch
    = !!user
      && !!overview
      && (!!viewerUsername || overview.profile.isAdministrator)
      && !isSelf
  const searchQuery = useQuery({
    ...trpc.socialRouter.searchUsers.queryOptions({
      query: publicUsername,
      limit: 1,
    }),
    enabled: canSearch,
  })
  const exactUser = (
    searchQuery.data as { users?: SocialSearchUser[] } | undefined
  )?.users?.find(candidate => candidate.username === publicUsername)
  const relationshipMutation = useMutation(
    trpc.socialRouter.changeRelationship.mutationOptions(),
  )
  const invalidateSocial = useInvalidateSocial()
  const { publishDataEvent } = useSocialWorkspace()

  const runAction = async (action: RelationshipAction) => {
    try {
      await relationshipMutation.mutateAsync(action)
      publishDataEvent('relationship.changed')
      await invalidateSocial()
      toast.success(t('social.toast.relationshipUpdated'))
    }
    catch {
      toast.error(t('social.toast.actionFailed'))
    }
  }

  if (!authResolved || (!!user && overviewQuery.isPending)) {
    return (
      <Button type="button" size="sm" variant="outline" disabled>
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        <span className="sr-only">{t('social.loading')}</span>
      </Button>
    )
  }

  if (!user) {
    const redirect = `${location.pathname}${location.search}${location.hash}`
    return (
      <Button asChild size="sm" variant="outline">
        <Link
          to={`/signin?redirect=${encodeURIComponent(redirect)}`}
          data-testid="public-player-signin-to-add"
        >
          <UserPlus className="size-4" aria-hidden="true" />
          {t('publicPlayerProfile.signInToAddFriend')}
        </Link>
      </Button>
    )
  }

  if (isSelf)
    return null

  if (overview && !viewerUsername && !overview.profile.isAdministrator) {
    return (
      <Button asChild size="sm" variant="outline">
        <Link
          to="/dashboard?tab=profile"
          data-testid="public-player-choose-username"
        >
          <UserPlus className="size-4" aria-hidden="true" />
          {t('publicPlayerProfile.chooseUsernameToAddFriend')}
        </Link>
      </Button>
    )
  }

  if (
    !canSearch
    || searchQuery.isError
    || (!searchQuery.isPending && !exactUser)
  ) {
    return null
  }

  if (searchQuery.isPending) {
    return (
      <Button type="button" size="sm" variant="outline" disabled>
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        <span className="sr-only">{t('social.loading')}</span>
      </Button>
    )
  }

  if (!exactUser)
    return null

  const pending = relationshipMutation.isPending
  const button = (
    action: RelationshipAction,
    label: string,
    icon: ReactNode,
    variant: 'default' | 'outline' | 'ghost' = 'outline',
  ) => (
    <Button
      type="button"
      size="sm"
      variant={variant}
      disabled={pending}
      onClick={() => runAction(action)}
      data-testid="public-player-friend-action"
      data-relationship={exactUser.relationship}
    >
      {pending ? (
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
      ) : (
        icon
      )}
      {label}
    </Button>
  )

  if (exactUser.relationship === 'friend') {
    return (
      <Badge
        variant="secondary"
        className="h-9 gap-1.5 px-3"
        data-testid="public-player-friend-action"
        data-relationship="friend"
      >
        <Check className="size-4" aria-hidden="true" />
        {t('social.friends.connected')}
      </Badge>
    )
  }
  if (exactUser.relationship === 'incoming' && exactUser.connectionId) {
    return button(
      { action: 'accept', connectionId: exactUser.connectionId },
      t('social.actions.accept'),
      <Check className="size-4" aria-hidden="true" />,
      'default',
    )
  }
  if (exactUser.relationship === 'outgoing' && exactUser.connectionId) {
    return button(
      { action: 'cancel', connectionId: exactUser.connectionId },
      t('social.actions.cancel'),
      <X className="size-4" aria-hidden="true" />,
      'ghost',
    )
  }
  if (exactUser.relationship === 'blockedByMe') {
    return button(
      { action: 'unblock', targetUserId: exactUser.id },
      t('social.actions.unblock'),
      <UserPlus className="size-4" aria-hidden="true" />,
      'ghost',
    )
  }
  return button(
    { action: 'request', targetUserId: exactUser.id },
    t('social.actions.add'),
    <UserPlus className="size-4" aria-hidden="true" />,
    'default',
  )
}

function useNoindexPage(username: string, language: LanguageCode) {
  useLayoutEffect(() => applyArenaDynamicHead({
    title: `@${username} | Mentiss`,
    description: `Public player profile for @${username} on Mentiss Arena.`,
    language: arenaLanguageTag(language),
    robots: arenaSeoRobots(false),
    canonical: `${arenaSeoOrigin()}${withLanguagePath(`/players/${encodeURIComponent(username.toLowerCase())}`, language)}`,
  }), [language, username])
}

export function PublicPlayerProfilePage() {
  const { username = '' } = useParams<{ username: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { language, t } = useI18n()
  const trpc = useTRPC()
  const profileQuery = useQuery(
    trpc.publicPlayerProfileRouter.get.queryOptions({ username }),
  )
  const canonicalUsername = profileQuery.data?.username

  useNoindexPage(canonicalUsername ?? username, language)

  useEffect(() => {
    if (!canonicalUsername || username === canonicalUsername)
      return

    navigate(
      {
        pathname: `/players/${encodeURIComponent(canonicalUsername)}`,
        search: location.search,
        hash: location.hash,
      },
      { replace: true },
    )
  }, [canonicalUsername, location.hash, location.search, navigate, username])

  if (profileQuery.isPending) {
    return (
      <main
        className="mx-auto w-full max-w-4xl px-4 pb-16 pt-28 sm:px-6"
        aria-busy="true"
        data-testid="public-player-loading"
      >
        <div className="animate-pulse space-y-6">
          <div className="h-20 rounded-2xl bg-muted" />
          <div className="grid grid-cols-2 gap-3">
            <div className="h-28 rounded-xl bg-muted" />
            <div className="h-28 rounded-xl bg-muted" />
          </div>
          <div className="h-52 rounded-xl bg-muted" />
        </div>
        <span className="sr-only">{t('publicPlayerProfile.loading')}</span>
      </main>
    )
  }

  const errorCode = (profileQuery.error as { data?: { code?: string } } | null)
    ?.data
    ?.code
  if (profileQuery.isError && errorCode === 'NOT_FOUND') {
    return (
      <main
        className="mx-auto flex min-h-[70vh] w-full max-w-xl items-center px-4 pb-16 pt-24 sm:px-6"
        data-testid="public-player-not-found"
      >
        <Card className="w-full shadow-none">
          <CardContent className="flex flex-col items-center px-6 py-10 text-center sm:py-14">
            <div className="mb-5 flex size-14 items-center justify-center rounded-full bg-muted">
              <SearchX
                className="size-7 text-muted-foreground"
                aria-hidden="true"
              />
            </div>
            <p className="text-sm font-semibold text-muted-foreground">404</p>
            <h1 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">
              {t('publicPlayerProfile.notFoundTitle')}
            </h1>
            <p className="mt-3 max-w-sm text-sm text-muted-foreground sm:text-base">
              {t('publicPlayerProfile.notFoundDescription')}
            </p>
          </CardContent>
        </Card>
      </main>
    )
  }

  if (profileQuery.isError) {
    return (
      <main
        className="mx-auto flex min-h-[70vh] w-full max-w-xl items-center px-4 pb-16 pt-24 sm:px-6"
        data-testid="public-player-error"
      >
        <Card className="w-full shadow-none">
          <CardContent className="flex flex-col items-center px-6 py-10 text-center sm:py-14">
            <div className="mb-5 flex size-14 items-center justify-center rounded-full bg-destructive/10">
              <RefreshCw
                className="size-7 text-destructive"
                aria-hidden="true"
              />
            </div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
              {t('publicPlayerProfile.errorTitle')}
            </h1>
            <p className="mt-3 max-w-sm text-sm text-muted-foreground sm:text-base">
              {t('publicPlayerProfile.errorDescription')}
            </p>
            <Button
              type="button"
              variant="outline"
              className="mt-6"
              onClick={() => profileQuery.refetch()}
            >
              <RefreshCw className="size-4" aria-hidden="true" />
              {t('publicPlayerProfile.retry')}
            </Button>
          </CardContent>
        </Card>
      </main>
    )
  }

  if (!profileQuery.data) {
    return null
  }

  const { username: publicUsername, stats, recentGames } = profileQuery.data
  const winRate
    = stats.winRate === null ? '—' : `${Math.round(stats.winRate * 100)}%`

  return (
    <main className="mx-auto w-full max-w-4xl px-4 pb-16 pt-24 sm:px-6 sm:pt-28">
      <header className="mb-8 flex flex-wrap items-start gap-4 sm:items-center sm:gap-5">
        <div className="flex size-16 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary sm:size-20">
          <UserRound className="size-8 sm:size-10" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-muted-foreground">
            {t('publicPlayerProfile.eyebrow')}
          </p>
          <h1
            className="break-words text-3xl font-bold tracking-tight text-foreground sm:text-4xl"
            data-testid="public-player-username"
          >
            @{publicUsername}
          </h1>
        </div>
        <div className="flex basis-full justify-end empty:hidden sm:ml-auto sm:basis-auto">
          <PublicProfileFriendAction publicUsername={publicUsername} />
        </div>
      </header>

      <section
        className="grid grid-cols-2 gap-3 sm:gap-4"
        aria-label={t('publicPlayerProfile.statistics')}
      >
        <Card className="gap-3 py-5 shadow-none">
          <CardContent className="px-5 sm:px-6">
            <Gamepad2
              className="mb-4 size-5 text-muted-foreground"
              aria-hidden="true"
            />
            <p
              className="text-3xl font-bold tabular-nums sm:text-4xl"
              data-testid="public-player-games-played"
            >
              {stats.gamesPlayed}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('publicPlayerProfile.gamesPlayed')}
            </p>
          </CardContent>
        </Card>

        <Card className="gap-3 py-5 shadow-none">
          <CardContent className="px-5 sm:px-6">
            <Trophy
              className="mb-4 size-5 text-muted-foreground"
              aria-hidden="true"
            />
            <p
              className="text-3xl font-bold tabular-nums sm:text-4xl"
              data-testid="public-player-win-rate"
            >
              {winRate}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('publicPlayerProfile.winRate')}
            </p>
          </CardContent>
        </Card>
      </section>

      <section className="mt-6" aria-labelledby="public-player-record-title">
        <div className="mb-3 flex items-end justify-between gap-4">
          <div>
            <h2
              id="public-player-record-title"
              className="text-lg font-semibold text-foreground"
            >
              {t('publicPlayerProfile.record')}
            </h2>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {t('publicPlayerProfile.decidedExplanation', {
                decidedGames: stats.decidedGames,
                wins: stats.wins,
              })}
            </p>
          </div>
        </div>
        <Card className="gap-0 py-0 shadow-none">
          <CardContent className="grid grid-cols-3 divide-x divide-border px-0 text-center">
            <div className="px-2 py-4 sm:px-6">
              <p
                className="text-xl font-bold tabular-nums text-emerald-600 dark:text-emerald-400"
                data-testid="public-player-wins"
              >
                {stats.wins}
              </p>
              <p className="mt-1 text-xs text-muted-foreground sm:text-sm">
                {t('publicPlayerProfile.wins')}
              </p>
            </div>
            <div className="px-2 py-4 sm:px-6">
              <p
                className="text-xl font-bold tabular-nums text-rose-600 dark:text-rose-400"
                data-testid="public-player-losses"
              >
                {stats.losses}
              </p>
              <p className="mt-1 text-xs text-muted-foreground sm:text-sm">
                {t('publicPlayerProfile.losses')}
              </p>
            </div>
            <div className="px-2 py-4 sm:px-6">
              <p
                className="text-xl font-bold tabular-nums text-muted-foreground"
                data-testid="public-player-unscored"
              >
                {stats.unscored}
              </p>
              <p className="mt-1 text-xs text-muted-foreground sm:text-sm">
                {t('publicPlayerProfile.unscored')}
              </p>
            </div>
          </CardContent>
        </Card>
      </section>

      {recentGames.length === 0 ? (
        <Card className="mt-6 shadow-none" data-testid="public-player-empty">
          <CardContent className="flex flex-col items-center px-6 py-8 text-center sm:py-12">
            <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-muted">
              <Gamepad2
                className="size-6 text-muted-foreground"
                aria-hidden="true"
              />
            </div>
            <h2 className="text-lg font-semibold">
              {t('publicPlayerProfile.emptyTitle')}
            </h2>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              {t('publicPlayerProfile.emptyDescription')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <section className="mt-8" aria-labelledby="public-player-recent-title">
          <h2
            id="public-player-recent-title"
            className="mb-3 text-lg font-semibold text-foreground"
          >
            {t('publicPlayerProfile.recentGames')}
          </h2>
          <Card className="gap-0 py-0 shadow-none">
            <ul className="divide-y divide-border">
              {recentGames.map((game) => {
                const outcomeLabel
                  = game.outcome === 'win'
                    ? t('publicPlayerProfile.outcomeWin')
                    : game.outcome === 'loss'
                      ? t('publicPlayerProfile.outcomeLoss')
                      : t('publicPlayerProfile.outcomeUnscored')
                const outcomeClass
                  = game.outcome === 'win'
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300'
                    : game.outcome === 'loss'
                      ? 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300'
                      : 'border-border bg-muted text-muted-foreground'
                const startedAt = formatStartedAt(game.startedAt, language)
                const parsedSetting = parseGameSettingToPlayerConfig(
                  game.gameSetting,
                )
                const settingTitle = gameSettingTitle(game.gameSetting, t)
                const settingComposition = parsedSetting
                  ? formatGameSettingDisplay(parsedSetting, t)
                  : null

                return (
                  <li key={game.gameId}>
                    <a
                      href={withLanguagePath(
                        getStartedGameRoute(game.gameId, game.gameSetting),
                        language,
                      )}
                      className="group flex min-h-20 items-start gap-3 p-4 transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:items-center sm:gap-4 sm:px-6"
                      data-testid="public-player-game-row"
                    >
                      <Badge
                        variant="outline"
                        className={`min-w-20 ${outcomeClass}`}
                      >
                        {outcomeLabel}
                      </Badge>
                      <div className="min-w-0 flex-1">
                        <p
                          className="font-semibold text-foreground"
                          data-testid="public-player-game-setting-title"
                        >
                          {settingTitle}
                        </p>
                        {parsedSetting && (
                          <p
                            className="mt-1 text-xs font-medium text-muted-foreground sm:text-sm"
                            data-testid="public-player-game-player-count"
                          >
                            {t('play.players', {
                              count: parsedSetting.playerCount,
                            })}
                          </p>
                        )}
                        {settingComposition && (
                          <p
                            className="mt-1 text-xs leading-relaxed text-muted-foreground sm:text-sm"
                            data-testid="public-player-game-composition"
                          >
                            {settingComposition}
                          </p>
                        )}
                        <p className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground sm:text-sm">
                          <CalendarDays
                            className="size-3.5"
                            aria-hidden="true"
                          />
                          {t('publicPlayerProfile.startedOn', {
                            date: startedAt,
                          })}
                        </p>
                      </div>
                      <ArrowUpRight
                        className="size-5 shrink-0 text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                        aria-hidden="true"
                      />
                    </a>
                  </li>
                )
              })}
            </ul>
          </Card>
        </section>
      )}
    </main>
  )
}
